import { useState, useEffect, useCallback, useMemo } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getGridcoIntraGen,
  getGridcoIsgs,
  getGridcoRegen,
} from "../../../Rest_api/RestApi";
import { R_CHART_TYPES } from "../const";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { GenerationCommon } from "../../../models/gridco";
import { BrushStart } from "../../../models/chart_model";
import { SearchBox } from "../../../components/Search";
import { titleCase } from "../../../extensions/extension";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { stringToConsistentColor } from "../../../extensions/string";

const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[1], R_CHART_TYPES[2]];
const GROUP_BY = [
  { lable: "Ownership", value: "ownership" },
  { lable: "Generator Type", value: "generator_type" },
  { lable: "Station", value: "station_name" },
];
const UNITS = ["MW", "MWh"];

const INTERVAL = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
  {
    label: "Generator Type",
    key: "generator_type",
    name: "Generator Type",
    firstKey: "generator_type",
    secondKey: undefined,
    values: ["generator_type"],
    isTime: false,
  },
  {
    label: "Station",
    key: "station_name",
    name: "Station",
    firstKey: "station_name",
    secondKey: undefined,
    values: ["station_name"],
    isTime: false,
  },
];

export function GridcoGenrationOverview() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [data, setData] = useState<GenerationCommon[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueSource, setUniqueSource] = useState<string[]>([]);
  const [uniqueStations, setUniqueStations] = useState<string[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [selectedSource, setSelectedSource] = useState<string[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[2]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedXaxis, setSelectedXaxis] = useState(INTERVAL[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0].value);
  const [loading, setLoading] = useState(true);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const RegenData = await getGridcoRegen({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });

    const IntraData = await getGridcoIntraGen({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });

    const IsgsData = await getGridcoIsgs({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });

    const transformedIsgsData = IsgsData.map((item) => ({
      ...item,
      value_mw: item.experi_value,
      value_mwh: item.experi_value / 4,
    }));

    const transformedIntraData = IntraData.map((item) => ({
      ...item,
      value_mw: item.experi_value,
      value_mwh: item.experi_value / 4,
    }));

    const newData: GenerationCommon[] = [
      ...transformedIntraData,
      ...transformedIsgsData,
      ...RegenData,
    ];
    setData(newData);
    setUniqueStations(
      Array.from(
        new Set(
          newData
            .map(
              (item) =>
                item.station_name || item.sub_station || item.beneficiery_from
            )
            .filter((station) => station !== undefined && station !== null)
        )
      )
    );

    setUniqueSource(
      Array.from(new Set(newData.map((item) => titleCase(item.generator_type))))
    );
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.time_block))));
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    const processedData = data.map((item) => ({
      ...item,
      value_mw: item.value_mw || 0,
      value_mwh: item.value_mw / 4,
      month: getMonthFromDate(item.date),
      slot: item.time_block,
      generator_type:
        item.generator_type.charAt(0).toUpperCase() +
        item.generator_type.slice(1),
      station_name:
        item.station_name || item.sub_station || item.beneficiery_from,
    }));

    let filteredData = processedData;

    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }

    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate
    );

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }

    if (selectedSource.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedSource.includes(titleCase(item.generator_type))
      );
    }

    if (selectedStation.length > 0) {
      filteredData = filteredData.filter((item) => {
        return selectedStation.map((i) => i.label).includes(item.station_name);
      });
    }

    function getKeys() {
      if (selectedXaxis.key === "time") {
        return ["date", "slot"];
      } else if (selectedXaxis.key === "date") {
        return ["date"];
      } else {
        return [selectedXaxis.values[0]];
      }
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "slot"],
      groupBy: [selectedGroupKeys],
      volumeKey: "",
      aggregationKeys: ["value_mw", "value_mwh"],
      priceKey: "",
      data: filteredData,
      averageKeys: ["value_mw"],
      averageDuplicationKeys: ["date", "slot"],
      reverseSort: ["date", "slot"],
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    DefaultDates,
    activeTab,
    data,
    selectedBlock,
    selectedGroupKeys,
    selectedSource,
    selectedStation,
    selectedXaxis,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index].value);
  }

  const handleKeyChange = (index: number, setSelectedUnit: any) => {
    // if (INTERVAL[index].firstKey != "slot") {
    //   setSelectedUnit(UNITS[1]);
    // }
    setSelectedUnit("MW");
    setSelectedXaxis(INTERVAL[index]);
  };

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedXaxis.key;
    }
  }, [selectedBlock.length, selectedXaxis.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedXaxis.isTime
    ? [
        "slot", // Changed dateIndex to date
        selectedXaxis.values[1],
      ]
    : [selectedXaxis.values[0]];

  const summaryExtraLabels = selectedXaxis.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  function getLegends() {
    return chartKeys.map((item, index) => ({
      dataKey: item + "_value_" + selectedUnit.toLowerCase(),
      name: item,
      legendColor: undefined,
      stroke: stringToConsistentColor(item),
      type: selectedChartType.value,
      stackId: selectedChartType.stackId,
    }));
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Generation Overview",
  };

  function secondDataKey() {
    if (selectedXaxis.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X Axis"
            items={INTERVAL.map((item) => item.name)}
            selectedItems={[selectedXaxis.name]}
            onSelect={(item, index) => {
              handleKeyChange(index, setSelectedUnit);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.lable)}
            selectedItems={[
              GROUP_BY.find((g) => g.value === selectedGroupKeys)?.lable || "",
            ]}
            onSelect={(item, index) => {
              _handleGroupByChange(index);
            }}
          />

          <FilterMenu
            title="Generator Type"
            items={uniqueSource}
            selectedItems={selectedSource}
            setSelectedItems={setSelectedSource}
          />

          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading">
            <h2 className="chartHeading w-80">Generation Overview</h2>
            <div className="flex gap-x-2 w-full justify-end">
              <SearchBox
                options={uniqueBlock.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />

              <SearchBox
                options={uniqueStations.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Station"
                selectedOptions={selectedStation}
                onChange={setSelectedStation}
              />

              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  //

                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);

                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              xAxisTickMargin={10}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedXaxis.firstKey === "slot"}
              onlyTitle={true}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey={xDataKey}
              xTick={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={secondDataKey()}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  1. SLDC: Daily CGP scheduling <br /> 2.ERLDC: Daily final
                  scheduling <br /> 3. ABT Meter readings (W exp) :
                  Solar/SHEP/Biomass. <br /> 4. Gridco : Vedanta IPP, OPGC Stage
                  II, State Hydro.
                </p>
              }
              text="1. All scheduling to Odisha Solar beneficiary is considered hybrid power.
              2. OPGC Stage I taken from state hydro file (IB Unit I & II).
              3. Solar/SHEP/Biomass has been named mapped.
              4. OPGC Stage II Entitlement is taken from Declared Capacity.
              5. Hydro Entitlement is same as Expp.
              6. Vedanta Entitlement is taken from Total Injection (considering backdown).
              7. Wind power generation is taken from ERLDC (GNA & LTA).
              
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
